import { XCircle } from "lucide-react";
import { Button } from "@components/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog";
import useTranslator from "@pma/hooks/use-translator";
import { BILL_DECLINE_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@pma/hooks/use-toast";
import { Label } from "@components/common/label";
import { Textarea } from "@components/common/textarea";
import { useState } from "react";
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers";
import { useNavigate, useSearchParams } from "react-router-dom";

interface BillDeclineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billId: string;
  onDecline: CallableFunction;
}

export function BillDeclineDialog({
  open,
  onOpenChange,
  billId,
  onDecline,
}: BillDeclineDialogProps) {
  const [reason, setReason] = useState("");

  const { translate, locale } = useTranslator();
  const navigate = useNavigate();
  const [searchParam] = useSearchParams();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const isHebrew = locale === "he";

  const handleCancel = () => {
    onOpenChange(false);
  };

  const declineBill = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILL_DECLINE_ENDPOINT,
      {
        bill_id: billId,
        bill_message_text: reason,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const handleBillDeclineMutation = useMutation({
    mutationKey: ["decline-bill"],
    mutationFn: declineBill,
    onSuccess: () => {
      onDecline();
      toast({
        title: translate("Success"),
        description: translate("bill declined successfully"),
        variant: "success",
      });
      const newParams = new URLSearchParams(searchParam);
      newParams.set("revalidate", "bills");
      newParams.set("bill_tab", "declined");
      navigate(`?${newParams.toString()}`);
      handleCancel();
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "decline bill", translate),
        variant: "destructive",
      });
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[550px]' dir={isHebrew ? "rtl" : "ltr"}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <XCircle className='h-5 w-5 text-destructive' />
            {translate("Decline Bill")}
          </DialogTitle>
          <DialogDescription className={cn(isHebrew ? "text-right": "text-left")}>
            {translate("Decline bill and provide feedback for revision")}.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-6 py-4'>
          <div className='grid gap-4'>
            <div className='grid gap-2'>
              <Label htmlFor='decline-reason'>{translate("Detailed Reason")}</Label>
              <Textarea
                id='decline-reason'
                placeholder={
                  translate("Provide specific details about why this bill is being declined") +
                  "..."
                }
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={5}
              />
            </div>
          </div>
        </div>
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={handleCancel}>
            {translate("Cancel")}
          </Button>
          <Button
            variant='destructive'
            onClick={() => handleBillDeclineMutation.mutate()}
            disabled={handleBillDeclineMutation.isPending}
            loading={handleBillDeclineMutation.isPending}
          >
            <XCircle className={cn('h-4 w-4', isHebrew ? "ml-1" : "mr-1")} />
            {translate("Decline Bill")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
